// Estructura de una plantilla de informe: lista PLANA y ordenada de filas con su
// grupo, su subgrupo y su obligatoriedad. Es la forma exacta en que la API
// guarda y devuelve la plantilla (`filas`), y todo el editor opera sobre ella.
//
// Por qué una lista plana y no un árbol: el orden de la plantilla ES el orden
// del informe, y el árbol (grupo › subgrupo › ítem) se deduce de la lista, no
// al revés. Una estructura con dos fuentes —el árbol y el orden— se
// contradice sola; con una sola lista, "mover" es siempre reordenar filas.
//
// Dos reglas de forma que todo el módulo mantiene:
//
// - **Los grupos son CONTIGUOS** y dentro de un grupo los subgrupos también.
//   El árbol se arma recorriendo la lista una vez, así que una fila fuera de
//   lugar partiría su grupo en dos sin que nada fallara.
// - **Un grupo o subgrupo vacío existe como MARCADOR** (`item: null`). Sin él,
//   al vaciar un grupo desaparecería de la plantilla, y el tasador que lo
//   acaba de crear (o de vaciar a propósito) lo perdería de vista.
//
// Todas las funciones son PURAS: reciben la lista y devuelven una nueva (o
// `null` cuando no hay nada que mover). Los algoritmos son el port de los del
// prototipo de Claude Design, con una corrección: la posición de un marcador
// que queda atrás se ajusta al borde de grupo más cercano (`colocarMarcador`),
// porque el cálculo por índice solo del prototipo podía dejarlo en medio de
// otro grupo.

export type Obligatoriedad = "obligatorio" | "condicional" | "opcional";

export interface Fila {
  /**
   * Referencia al ítem (id del maestro como texto, o `tmp:…` para un ítem
   * creado en esta sesión y aún sin guardar). `null` es un MARCADOR de grupo o
   * subgrupo vacío.
   */
  item: string | null;
  grupo: string;
  /** `null` es "sin subgrupo". */
  subgrupo: string | null;
  obligatoriedad: Obligatoriedad | null;
  condicion: string;
}

type Predicado = (fila: Fila) => boolean;

const esMarcador = (fila: Fila) => fila.item === null;

/* ------------------------------------------------------------------ */
/* Utilidades de bajo nivel (operan sobre una copia)                   */
/* ------------------------------------------------------------------ */

function copiar(filas: Fila[]): Fila[] {
  return filas.map((fila) => ({ ...fila }));
}

function ultimoIndice(L: Fila[], pred: Predicado): number {
  let ultimo = -1;
  L.forEach((fila, k) => {
    if (pred(fila)) ultimo = k;
  });
  return ultimo;
}

interface Extraccion {
  salida: Fila[];
  /** Lugar donde estaba la primera fila extraída. */
  pos: number;
  /** Grupo de la fila que venía justo después de lo extraído. */
  siguiente: string | undefined;
}

/** Saca de la lista las filas que cumplen `pred`, en orden, y dice de dónde. */
function extraer(L: Fila[], pred: Predicado): Extraccion {
  const salida: Fila[] = [];
  let pos = -1;
  for (let k = L.length - 1; k >= 0; k--) {
    if (pred(L[k])) {
      salida.unshift(L[k]);
      L.splice(k, 1);
      pos = k;
    }
  }
  return { salida, pos, siguiente: L[pos]?.grupo };
}

/**
 * Pone un marcador en `pos` sin partir un grupo.
 *
 * El índice que calculan los algoritmos de movimiento es una ESTIMACIÓN: si
 * justo ahí la fila que entró se pegó al grupo vecino, el marcador caería en
 * medio de ese grupo. Se lleva entonces al borde de la corrida de ese grupo:
 * al comienzo si es el que seguía a lo que se movió, al final si no.
 */
function colocarMarcador(L: Fila[], marcador: Fila, pos: number, siguiente: string | undefined) {
  let en = Math.max(0, Math.min(pos, L.length));
  if (en > 0 && en < L.length && L[en - 1].grupo === L[en].grupo) {
    const grupo = L[en].grupo;
    let inicio = en;
    while (inicio > 0 && L[inicio - 1].grupo === grupo) inicio--;
    let fin = en;
    while (fin < L.length && L[fin].grupo === grupo) fin++;
    en = grupo === siguiente ? inicio : fin;
  }
  L.splice(en, 0, marcador);
}

/** Quita los marcadores que dejaron de hacer falta o que quedaron repetidos. */
function normalizar(L: Fila[]) {
  for (let k = L.length - 1; k >= 0; k--) {
    const fila = L[k];
    if (!esMarcador(fila)) continue;

    const repetido =
      L.findIndex(
        (x) => esMarcador(x) && x.grupo === fila.grupo && x.subgrupo === fila.subgrupo,
      ) !== k;
    const lleno =
      fila.subgrupo !== null
        ? L.some((x) => !esMarcador(x) && x.grupo === fila.grupo && x.subgrupo === fila.subgrupo)
        : L.some((x) => x !== fila && x.grupo === fila.grupo);

    if (repetido || lleno) L.splice(k, 1);
  }
}

/** Si el lugar de origen quedó sin filas, deja un marcador que lo recuerde. */
function dejar(
  L: Fila[],
  viejo: { grupo: string; subgrupo: string | null },
  pos: number,
  siguiente: string | undefined,
) {
  if (L.some((x) => x.grupo === viejo.grupo && x.subgrupo === viejo.subgrupo)) return;

  if (viejo.subgrupo !== null) {
    colocarMarcador(L, marcadorDe(viejo.grupo, viejo.subgrupo), pos, siguiente);
  } else if (!L.some((x) => x.grupo === viejo.grupo)) {
    colocarMarcador(L, marcadorDe(viejo.grupo, null), pos, siguiente);
  }
}

/** Garantiza que el grupo no desaparezca cuando se le llevan todas sus filas. */
function conservarGrupo(L: Fila[], grupo: string, pos: number, siguiente: string | undefined) {
  if (!L.some((x) => x.grupo === grupo)) {
    colocarMarcador(L, marcadorDe(grupo, null), pos, siguiente);
  }
}

function marcadorDe(grupo: string, subgrupo: string | null): Fila {
  return { item: null, grupo, subgrupo, obligatoriedad: null, condicion: "" };
}

function trabajar(filas: Fila[], fn: (L: Fila[]) => void): Fila[] {
  const L = copiar(filas);
  fn(L);
  return L;
}

/** Subgrupos de un grupo en su orden, sin contar el marcador del grupo vacío. */
function subgruposDe(L: Fila[], grupo: string): (string | null)[] {
  const vistos: (string | null)[] = [];
  for (const fila of L) {
    if (fila.grupo !== grupo) continue;
    if (esMarcador(fila) && fila.subgrupo === null) continue;
    if (!vistos.includes(fila.subgrupo)) vistos.push(fila.subgrupo);
  }
  return vistos;
}

function gruposDe(L: Fila[]): string[] {
  return [...new Set(L.map((x) => x.grupo))];
}

/* ------------------------------------------------------------------ */
/* Obligatoriedad                                                      */
/* ------------------------------------------------------------------ */

export interface ConteoDeObligatoriedad {
  obligatorios: number;
  condicionales: number;
  opcionales: number;
}

export function conteoPorObligatoriedad(
  filas: Fila[],
): ConteoDeObligatoriedad & { total: number } {
  const conteo = { total: 0, obligatorios: 0, condicionales: 0, opcionales: 0 };
  for (const fila of filas) {
    if (esMarcador(fila)) continue;
    conteo.total++;
    if (fila.obligatoriedad === "obligatorio") conteo.obligatorios++;
    else if (fila.obligatoriedad === "condicional") conteo.condicionales++;
    else if (fila.obligatoriedad === "opcional") conteo.opcionales++;
  }
  return conteo;
}

/** El clic en la píldora: O → C → P → O. "Quitar" es otra acción, no un paso. */
export function siguienteObligatoriedad(actual: Obligatoriedad): Obligatoriedad {
  if (actual === "obligatorio") return "condicional";
  if (actual === "condicional") return "opcional";
  return "obligatorio";
}

/* ------------------------------------------------------------------ */
/* Mover ítems                                                         */
/* ------------------------------------------------------------------ */

/**
 * Un paso arriba (`-1`) o abajo (`1`). Con un vecino del mismo subgrupo se
 * intercambian; en el borde el ítem ADOPTA la ubicación del vecino, y si deja
 * su subgrupo (o grupo) vacío queda un marcador. `null` si no hay hacia dónde.
 */
export function moverItemUnPaso(filas: Fila[], item: string, dir: -1 | 1): Fila[] | null {
  const idx = filas.findIndex((f) => f.item === item);
  const j = idx + dir;
  if (idx < 0 || j < 0 || j >= filas.length) return null;

  return trabajar(filas, (L) => {
    const p = L[idx];
    const q = L[j];

    if (!esMarcador(q) && q.grupo === p.grupo && q.subgrupo === p.subgrupo) {
      L[idx] = q;
      L[j] = p;
      return;
    }

    const viejo = { grupo: p.grupo, subgrupo: p.subgrupo };
    p.grupo = q.grupo;
    p.subgrupo = q.subgrupo;
    dejar(L, viejo, dir < 0 ? idx + 1 : idx, filas[idx + 1]?.grupo);
    normalizar(L);
  });
}

/** Al final de un subgrupo (o, si no tiene filas, al comienzo de su grupo). */
export function moverItemAUbicacion(
  filas: Fila[],
  item: string,
  grupo: string,
  subgrupo: string | null,
): Fila[] {
  return trabajar(filas, (L) => {
    const { salida, pos, siguiente } = extraer(L, (f) => f.item === item);
    const p = salida[0];
    if (!p) return;
    const viejo = { grupo: p.grupo, subgrupo: p.subgrupo };
    p.grupo = grupo;
    p.subgrupo = subgrupo;

    let at = ultimoIndice(L, (x) => x.grupo === grupo && x.subgrupo === subgrupo);
    if (at < 0) {
      const primero = L.findIndex((x) => x.grupo === grupo);
      at = primero < 0 ? L.length - 1 : primero - 1;
    }
    L.splice(at + 1, 0, p);
    dejar(L, viejo, pos + (at + 1 <= pos ? 1 : 0), siguiente);
    normalizar(L);
  });
}

/** Lo coloca justo antes de otro ítem, en su mismo grupo y subgrupo. */
export function moverItemAntesDe(filas: Fila[], item: string, destino: string): Fila[] {
  if (item === destino) return filas;

  return trabajar(filas, (L) => {
    const { salida, pos, siguiente } = extraer(L, (f) => f.item === item);
    const p = salida[0];
    const tg = L.find((x) => x.item === destino);
    if (!p || !tg) return;
    const viejo = { grupo: p.grupo, subgrupo: p.subgrupo };
    p.grupo = tg.grupo;
    p.subgrupo = tg.subgrupo;

    const at = L.indexOf(tg);
    L.splice(at, 0, p);
    dejar(L, viejo, pos + (at <= pos ? 1 : 0), siguiente);
    normalizar(L);
  });
}

/* ------------------------------------------------------------------ */
/* Mover subgrupos                                                     */
/* ------------------------------------------------------------------ */

export interface ResultadoDeSubgrupo {
  filas: Fila[];
  /** Si el subgrupo cruzó a otro grupo, cuál: el aviso lo nombra. */
  grupoDestino?: string;
}

/**
 * Un paso dentro de su grupo; en el borde cruza al grupo vecino (al final del
 * anterior o al comienzo del siguiente).
 */
export function moverSubgrupoUnPaso(
  filas: Fila[],
  grupo: string,
  subgrupo: string | null,
  dir: -1 | 1,
): ResultadoDeSubgrupo | null {
  const subs = subgruposDe(filas, grupo);
  const vecino = subs[subs.indexOf(subgrupo) + dir];

  if (vecino !== undefined) {
    return {
      filas: trabajar(filas, (L) => {
        const { salida } = extraer(L, (x) => x.grupo === grupo && x.subgrupo === subgrupo);
        if (dir < 0) {
          L.splice(
            L.findIndex((x) => x.grupo === grupo && x.subgrupo === vecino),
            0,
            ...salida,
          );
        } else {
          L.splice(
            ultimoIndice(L, (x) => x.grupo === grupo && x.subgrupo === vecino) + 1,
            0,
            ...salida,
          );
        }
      }),
    };
  }

  const grupos = gruposDe(filas);
  const nuevo = grupos[grupos.indexOf(grupo) + dir];
  if (nuevo === undefined) return null;

  return {
    grupoDestino: nuevo,
    filas: trabajar(filas, (L) => {
      const { salida, siguiente } = extraer(L, (x) => x.grupo === grupo && x.subgrupo === subgrupo);
      salida.forEach((x) => {
        x.grupo = nuevo;
      });
      if (dir < 0) {
        const at = ultimoIndice(L, (x) => x.grupo === nuevo) + 1;
        L.splice(at, 0, ...salida);
        conservarGrupo(L, grupo, at + salida.length, siguiente);
      } else {
        const at = L.findIndex((x) => x.grupo === nuevo);
        L.splice(at, 0, ...salida);
        conservarGrupo(L, grupo, at, siguiente);
      }
      normalizar(L);
    }),
  };
}

/** Lo lleva al final de otro grupo, con todos sus ítems. */
export function moverSubgrupoAGrupo(
  filas: Fila[],
  grupo: string,
  subgrupo: string | null,
  destino: string,
): Fila[] {
  if (grupo === destino) return filas;

  return trabajar(filas, (L) => {
    const { salida, pos, siguiente } = extraer(
      L,
      (x) => x.grupo === grupo && x.subgrupo === subgrupo,
    );
    salida.forEach((x) => {
      x.grupo = destino;
    });
    const at = ultimoIndice(L, (x) => x.grupo === destino) + 1;
    L.splice(at, 0, ...salida);
    conservarGrupo(L, grupo, pos + (at <= pos ? salida.length : 0), siguiente);
    normalizar(L);
  });
}

/** Lo coloca justo antes de otro subgrupo (de su mismo grupo o de otro). */
export function moverSubgrupoAntesDe(
  filas: Fila[],
  grupo: string,
  subgrupo: string | null,
  grupoDestino: string,
  subgrupoDestino: string | null,
): Fila[] {
  if (grupo === grupoDestino && subgrupo === subgrupoDestino) return filas;

  return trabajar(filas, (L) => {
    const { salida, pos, siguiente } = extraer(
      L,
      (x) => x.grupo === grupo && x.subgrupo === subgrupo,
    );
    salida.forEach((x) => {
      x.grupo = grupoDestino;
    });
    const at = L.findIndex((x) => x.grupo === grupoDestino && x.subgrupo === subgrupoDestino);
    L.splice(at, 0, ...salida);
    conservarGrupo(L, grupo, pos + (at <= pos ? salida.length : 0), siguiente);
    normalizar(L);
  });
}

/* ------------------------------------------------------------------ */
/* Mover grupos                                                        */
/* ------------------------------------------------------------------ */

export function moverGrupoUnPaso(filas: Fila[], grupo: string, dir: -1 | 1): Fila[] | null {
  const grupos = gruposDe(filas);
  const vecino = grupos[grupos.indexOf(grupo) + dir];
  if (vecino === undefined) return null;

  return trabajar(filas, (L) => {
    const { salida } = extraer(L, (x) => x.grupo === grupo);
    if (dir < 0) {
      L.splice(
        L.findIndex((x) => x.grupo === vecino),
        0,
        ...salida,
      );
    } else {
      L.splice(ultimoIndice(L, (x) => x.grupo === vecino) + 1, 0, ...salida);
    }
  });
}

export function moverGrupoAntesDe(filas: Fila[], grupo: string, destino: string): Fila[] {
  if (grupo === destino) return filas;

  return trabajar(filas, (L) => {
    const { salida } = extraer(L, (x) => x.grupo === grupo);
    L.splice(
      L.findIndex((x) => x.grupo === destino),
      0,
      ...salida,
    );
  });
}

/* ------------------------------------------------------------------ */
/* Drag and drop                                                       */
/* ------------------------------------------------------------------ */

export type OrigenDeArrastre =
  | { tipo: "item"; item: string }
  | { tipo: "subgrupo"; grupo: string; subgrupo: string | null }
  | { tipo: "grupo"; grupo: string };

export type DestinoDeSoltado =
  | { tipo: "item"; item: string }
  /** Fila del grupo: lo que se suelta cae al final de su último subgrupo. */
  | { tipo: "grupo"; grupo: string; ultimoSubgrupo: string | null }
  /** Fila de un subgrupo (con ítems o vacío). */
  | { tipo: "subgrupo"; grupo: string; subgrupo: string | null };

/**
 * Qué pasa al soltar. Un ítem cae ANTES de otro ítem o AL FINAL de un
 * subgrupo/grupo; un subgrupo antes de otro subgrupo o al final de un grupo; un
 * grupo antes de otro grupo. Cualquier otra combinación no hace nada: la
 * pantalla ya no la ofrece como destino, esto es la red de seguridad.
 */
export function soltar(
  filas: Fila[],
  origen: OrigenDeArrastre,
  destino: DestinoDeSoltado,
): Fila[] {
  if (origen.tipo === "item") {
    if (destino.tipo === "item") return moverItemAntesDe(filas, origen.item, destino.item);
    if (destino.tipo === "grupo") {
      return moverItemAUbicacion(filas, origen.item, destino.grupo, destino.ultimoSubgrupo);
    }
    return moverItemAUbicacion(filas, origen.item, destino.grupo, destino.subgrupo);
  }

  if (origen.tipo === "subgrupo") {
    if (destino.tipo === "subgrupo") {
      return moverSubgrupoAntesDe(
        filas,
        origen.grupo,
        origen.subgrupo,
        destino.grupo,
        destino.subgrupo,
      );
    }
    if (destino.tipo === "grupo") {
      return moverSubgrupoAGrupo(filas, origen.grupo, origen.subgrupo, destino.grupo);
    }
    return filas;
  }

  if (destino.tipo === "grupo") return moverGrupoAntesDe(filas, origen.grupo, destino.grupo);
  return filas;
}

/* ------------------------------------------------------------------ */
/* Crear y quitar                                                      */
/* ------------------------------------------------------------------ */

export function existeGrupo(filas: Fila[], nombre: string): boolean {
  const buscado = nombre.trim().toLowerCase();
  return filas.some((f) => f.grupo.toLowerCase() === buscado);
}

export function existeSubgrupo(filas: Fila[], grupo: string, nombre: string): boolean {
  const buscado = nombre.trim().toLowerCase();
  return filas.some((f) => f.grupo === grupo && f.subgrupo?.toLowerCase() === buscado);
}

/** Un grupo nuevo nace vacío y al final: solo existe en esta plantilla. */
export function crearGrupo(filas: Fila[], nombre: string): Fila[] {
  return [...copiar(filas), marcadorDe(nombre, null)];
}

export function crearSubgrupo(filas: Fila[], grupo: string, nombre: string): Fila[] {
  return trabajar(filas, (L) => {
    L.splice(
      ultimoIndice(L, (x) => x.grupo === grupo) + 1,
      0,
      marcadorDe(grupo, nombre),
    );
    normalizar(L);
  });
}

/** Elimina el marcador de un grupo o subgrupo vacío. */
export function eliminarVacio(filas: Fila[], grupo: string, subgrupo: string | null): Fila[] {
  return trabajar(filas, (L) => {
    const k = L.findIndex((x) => esMarcador(x) && x.grupo === grupo && x.subgrupo === subgrupo);
    if (k < 0) return;
    L.splice(k, 1);
    if (subgrupo !== null) conservarGrupo(L, grupo, k, L[k]?.grupo);
  });
}

/** Quita un ítem de la plantilla. Un grupo que se vacía por eso desaparece. */
export function quitarFila(filas: Fila[], item: string): Fila[] {
  return filas.filter((f) => f.item !== item).map((f) => ({ ...f }));
}

/**
 * Agrega una fila al final de su subgrupo; si no existe, al final de su grupo;
 * si tampoco, al final de la plantilla.
 */
export function agregarFila(filas: Fila[], fila: Fila): Fila[] {
  return trabajar(filas, (L) => {
    let at = ultimoIndice(L, (x) => x.grupo === fila.grupo && x.subgrupo === fila.subgrupo);
    if (at < 0) at = ultimoIndice(L, (x) => x.grupo === fila.grupo);
    if (at < 0) at = L.length - 1;
    L.splice(at + 1, 0, { ...fila });
    normalizar(L);
  });
}

export interface ItemDelMaestro {
  ref: string;
  grupo: string;
  subgrupo: string | null;
}

/**
 * Inserta un ítem del maestro en su POSICIÓN NATURAL: dentro de su subgrupo,
 * después del último hermano que el maestro ordena antes que él; si no hay
 * ninguno, al comienzo del subgrupo; si el subgrupo no existe, al final del
 * grupo; si el grupo tampoco, al final de la plantilla.
 *
 * (El prototipo caía al final del grupo cuando el ítem iba antes que TODOS los
 * hermanos: el índice -1 que da "antes del primero" se confundía con "no
 * encontrado".)
 */
export function insertarDelMaestro(
  filas: Fila[],
  item: ItemDelMaestro,
  obligatoriedad: Obligatoriedad,
  condicionSugerida: string,
  ordenDe: (ref: string) => number,
): Fila[] {
  return trabajar(filas, (L) => {
    const orden = ordenDe(item.ref);
    const mismoSubgrupo = (x: Fila) => x.grupo === item.grupo && x.subgrupo === item.subgrupo;

    let at = -1;
    L.forEach((x, k) => {
      if (!esMarcador(x) && mismoSubgrupo(x) && ordenDe(x.item as string) < orden) at = k;
    });

    if (at < 0) {
      const primero = L.findIndex(mismoSubgrupo);
      if (primero >= 0) at = primero - 1;
      else at = ultimoIndice(L, (x) => x.grupo === item.grupo);
    }
    if (at < 0 && !L.some(mismoSubgrupo) && !L.some((x) => x.grupo === item.grupo)) {
      at = L.length - 1;
    }

    L.splice(at + 1, 0, {
      item: item.ref,
      grupo: item.grupo,
      subgrupo: item.subgrupo,
      obligatoriedad,
      condicion: obligatoriedad === "condicional" ? condicionSugerida : "",
    });
    normalizar(L);
  });
}

/* ------------------------------------------------------------------ */
/* Árbol para dibujar                                                  */
/* ------------------------------------------------------------------ */

export interface NodoDeSubgrupo {
  subgrupo: string | null;
  /** Solo el marcador: el subgrupo no tiene ítems. */
  vacio: boolean;
  /** Ítems del subgrupo, vistos o no por el filtro. */
  total: number;
  filas: { fila: Fila; indice: number }[];
}

export interface NodoDeGrupo {
  grupo: string;
  subgrupos: NodoDeSubgrupo[];
  conteo: ConteoDeObligatoriedad;
  /** Ítems del grupo, vistos o no por el filtro. */
  total: number;
  /** Ítems que pasan el filtro. */
  visibles: number;
}

/**
 * Arma el árbol recorriendo la lista una vez. `pasa` decide qué ítems se ven;
 * los totales y los conteos son SIEMPRE los reales, para poder decir "3 de 12".
 */
export function arbolDeLaEstructura(filas: Fila[], pasa: Predicado): NodoDeGrupo[] {
  const grupos: NodoDeGrupo[] = [];

  filas.forEach((fila, indice) => {
    let grupo = grupos[grupos.length - 1];
    if (!grupo || grupo.grupo !== fila.grupo) {
      grupo = {
        grupo: fila.grupo,
        subgrupos: [],
        conteo: { obligatorios: 0, condicionales: 0, opcionales: 0 },
        total: 0,
        visibles: 0,
      };
      grupos.push(grupo);
    }

    if (esMarcador(fila) && fila.subgrupo === null) return;

    let sub = grupo.subgrupos[grupo.subgrupos.length - 1];
    if (!sub || sub.subgrupo !== fila.subgrupo) {
      sub = { subgrupo: fila.subgrupo, vacio: false, total: 0, filas: [] };
      grupo.subgrupos.push(sub);
    }

    if (esMarcador(fila)) {
      sub.vacio = true;
      return;
    }

    grupo.total++;
    sub.total++;
    if (fila.obligatoriedad === "obligatorio") grupo.conteo.obligatorios++;
    else if (fila.obligatoriedad === "condicional") grupo.conteo.condicionales++;
    else if (fila.obligatoriedad === "opcional") grupo.conteo.opcionales++;

    if (pasa(fila)) {
      sub.filas.push({ fila, indice });
      grupo.visibles++;
    }
  });

  return grupos;
}
